import { useTracking } from 'react-tracking';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { cn } from '@simplywallst/ui-core/next';
import Carousel from '@/components/Carousel';
import { StyledEmbla } from '@/components/Carousel/styled';
import { NarrativeCardPopulated } from '@/components/NarrativeCard';

import { useNarrativeWeeklyPicks } from '../../hooks/useNarrativeWeeklyPicks';
import { NarrativeInvalidationBoundary } from '../../../NarrativeInvalidationBoundary';

export const NarrativeCommunitySpotlightList = () => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const { trackEvent } = useTracking();
  const { data } = useNarrativeWeeklyPicks();
  const narratives = data?.narrativeWeeklyPicks ?? [];
  const hasMoreThanOneNarrative = narratives.length > 1;

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    const { id, company } = narrative;
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: id,
      companyId: company?.id,
      uniqueSymbol: company?.tickerSymbol,
    });
  };

  if (narratives.length === 0) {
    return null;
  }

  return (
    <section className="mt-x1 grid w-full gap-x1 overflow-hidden sm:gap-x2">
      <h2 className="m-0 text-sm font-medium text-solid">Weekly Picks</h2>
      <div className="-mt-x2 grid">
        <NarrativeInvalidationBoundary
          onSetPrimary={({ invalidatePriority }) =>
            invalidatePriority(['narrative-weekly-picks'])
          }
        >
          <StyledEmbla>
            <Carousel
              align="center"
              containScroll="trimSnaps"
              showArrows={hasMoreThanOneNarrative}
              showGradient={hasMoreThanOneNarrative}
              slidesToScroll={isMobileScreen ? 1 : 2}
            >
              <div
                role="list"
                className={cn(
                  'grid w-full gap-x1',
                  hasMoreThanOneNarrative
                    ? 'grid-cols-[repeat(3,317px)]'
                    : 'grid-cols-[minmax(317px,500px)]'
                )}
              >
                {narratives.slice(0, 3).map((narrative) => (
                  <div role="listitem" key={narrative.id}>
                    <NarrativeCardPopulated
                      showCompanyIcon
                      narrative={narrative}
                      onClick={() => handleOnClick(narrative)}
                    />
                  </div>
                ))}
              </div>
            </Carousel>
          </StyledEmbla>
        </NarrativeInvalidationBoundary>
      </div>
    </section>
  );
};
